import React from 'react';
import { jsx } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import { BANNER_NAME as BANNER_NAME_COOKIE_CONSENT } from '@atlassian/jira-cookie-consent-banner/src/constants.tsx';
import { CookieConsentBannerAsync as CookieConsentBanner } from '@atlassian/jira-cookie-consent-banner/src/ui/CookieConsentBannerAsync.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { BANNER_NAME as BANNER_NAME_IPBLOCKLISTING } from '@atlassian/jira-ip-block-listing-banner/src/common/constants.tsx';
import { IPBlocklistingBannerAsync as BannerIPBlocklisting } from '@atlassian/jira-ip-block-listing-banner/src/utils.tsx';
import { Banner as AkBanner } from '@atlassian/jira-navigation-system/src/index.tsx';
import { useBanner } from '@atlassian/jira-platform-controllers-global-banner/src/controllers/index.tsx';
import { NAME as BANNER_NAME_ANNOUNCEMENT } from '@atlassian/jira-platform-ui-banners-announcement/src/common/constants.tsx';
import { BannerAnnouncementAsync as BannerAnnouncement } from '@atlassian/jira-platform-ui-banners-announcement/src/ui/BannerAnnouncementAsync.tsx';
import { BANNER_NAME as BANNER_NAME_TRANSITION } from '@atlassian/jira-platform-ui-banners-eoc-transition/src/common/constants.tsx';
import { BannerTransitionAsync } from '@atlassian/jira-platform-ui-banners-eoc-transition/src/ui/index.tsx';
import { BANNER_NAME as BANNER_NAME_MAIL_EXPIRY } from '@atlassian/jira-platform-ui-mail-expiry-reminder/src/constants.tsx';
import { MailExpiryBannerAsync as MailExpiryBanner } from '@atlassian/jira-platform-ui-mail-expiry-reminder/src/ui/index.tsx';
import { BANNER_NAME as BANNER_NAME_RETURN_TO_MOBILE } from '@atlassian/jira-return-to-mobile-banner/src/common/constants.tsx';
import { ReturnToMobileBanner as BannerReturnToMobile } from '@atlassian/jira-return-to-mobile-banner/src/utils.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { BANNER_NAME as BANNER_NAME_JPD_BLUEPRINTS_FEEDBACK } from '@atlassian/jira-polaris-component-blueprints-feedback-banner/src/constants.tsx';
import { AsyncJpdBlueprintsFeedbackBanner as JpdBlueprintsFeedbackBanner } from '@atlassian/jira-polaris-component-blueprints-feedback-banner/src/index.tsx';
import { BANNER_NAME as BANNER_NAME_AUTOMATION_LIMIT_AWARENESS } from '@atlassian/jira-standard-automation-limit-awareness-modal/src/common/constants.tsx';
import { AutomationLimitAwarenessBannerAsync as AutomationLimitAwarenessBanner } from '@atlassian/jira-standard-automation-limit-awareness-modal/src/ui/banner/index.tsx';
import { BANNER_NAME as BANNER_NAME_AUTO_UPGRADE_NOT_A_TRIAL } from '@atlassian/jira-auto-upgrade-not-a-trial/src/constants.tsx';

import { Cohort3BannerAsync } from '@atlassian/jira-platform-ui-banners-eoc-cohort3-customers-banner/src/ui/index.tsx';
import { COHORT3_CONSOLIDATION_BANNER } from '@atlassian/jira-platform-ui-banners-eoc-cohort3-customers-banner/src/common/constants.tsx';
import { AsyncAutoUpgradeAvoidDeactivationBanner } from '@atlassian/jira-auto-upgrade-not-a-trial/src/ui/avoid-deactivation-banner/index.tsx';
import type { BannerContent$key } from './__generated__/BannerContent.graphql';

/**
 * This ID is used in pollinator tests and by JPD. Do not change lightly.
 */
const BANNER_ID = 'page-layout.banner';

/**
 * Dynamically displays a banner based on the current context and feature flags. It supports several types of banners,
 * each serving a different purpose (e.g., announcements, cookie consent). The decision to display a particular banner is
 * made through a combination of fetched banner data and the evaluation of feature flags, ensuring that the presented banner
 * is relevant to the user's current interaction and system state.
 *
 *  ALL BANNER COMPONENTS MUST BE PACKAGES THAT ARE LOCATED IN:
 * jira/src/packages/platform/ui/banners
 *
 * Create new packages in this workspace. There is a ratchet rule that depends on this.
 *
 * Please make sure that entryFsSsrLiftImportToModule is set to true for any async banners
 */
export const BannerContent = ({
	bannerFragment,
}: {
	bannerFragment?: BannerContent$key | null;
}) => {
	const data = useFragment<BannerContent$key>(
		graphql`
			fragment BannerContent on Query
			@argumentDefinitions(
				bannerAnnouncementViaSpa: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/banner-announcement-via-spa.relayprovider"
				}
			) {
				jira {
					# eslint-disable-next-line @atlassian/relay/must-colocate-fragment-spreads
					...BannerAnnouncement @include(if: $bannerAnnouncementViaSpa)
				}
			}
		`,
		bannerFragment,
	);
	const banner = useBanner();
	const bannerName = banner.name;

	return banner.isEnabled ? (
		<UFOSegment name="banner">
			<AkBanner height={banner.height} testId={BANNER_ID} id={BANNER_ID}>
				{bannerName === BANNER_NAME_ANNOUNCEMENT && (
					<BannerAnnouncement jiraQueryFragment={data?.jira} />
				)}
				{bannerName === BANNER_NAME_IPBLOCKLISTING && <BannerIPBlocklisting />}
				{bannerName === BANNER_NAME_RETURN_TO_MOBILE && <BannerReturnToMobile />}
				{bannerName === BANNER_NAME_COOKIE_CONSENT && <CookieConsentBanner />}
				{bannerName === BANNER_NAME_TRANSITION && <BannerTransitionAsync />}
				{bannerName === BANNER_NAME_MAIL_EXPIRY && <MailExpiryBanner />}
				{bannerName === BANNER_NAME_JPD_BLUEPRINTS_FEEDBACK &&
					fg('jpd-view-templates-feedback-banner') && <JpdBlueprintsFeedbackBanner />}
				{bannerName === BANNER_NAME_AUTOMATION_LIMIT_AWARENESS &&
					fg('upsell_jira_global_automations_limit_banner_fg') && (
						<AutomationLimitAwarenessBanner />
					)}
				{bannerName === COHORT3_CONSOLIDATION_BANNER && fg('eoc_consolidation_cohort_3_banner') && (
					<Cohort3BannerAsync />
				)}
				{bannerName === BANNER_NAME_AUTO_UPGRADE_NOT_A_TRIAL &&
					fg('auto_upgrade_not_a_trial_jira_gate') && <AsyncAutoUpgradeAvoidDeactivationBanner />}
			</AkBanner>
		</UFOSegment>
	) : null;
};
